import React, { Component } from "react";
import { Link } from "react-router-dom";

// CSS
// The old .free-videos/.video cards came from ../Home/Home.scss, which this
// page no longer borrows from -- Home was rebuilt and does not use them either.
import "./FreeVideos.scss";

// Components
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";
import Icon from "../../components/Icon";

// Helper
import { apiFetch, resolveTenant, cachedTenant } from "../../Helper/Api";
import { t } from "../../Helper/i18n";

const properties = require("../../properties.json");

class FreeVideos extends Component {
  state = {
    tenant: cachedTenant(),
    notFound: false,
    lessons: [],
    // without this the page renders "no free lessons" for the length of the
    // request, on every visit
    loading: true,
  };

  componentDidMount() {
    resolveTenant()
      .then((tenant) => {
        document.title = tenant.name ? `${t("freeVideos")} | ${tenant.name}` : properties.site_name;
        this.setState({ tenant });
        return apiFetch("/lessons/free", { query: { teacherUserId: tenant.teacherUserId } });
      })
      .then((lessons) => this.setState({ lessons: lessons || [], loading: false }))
      .catch(() => this.setState({ notFound: true, loading: false }));
  }

  componentWillUnmount() {
    if (this.io) this.io.disconnect();
  }

  /* Reveal as each card is reached. The ref sits on the grid, which only
     exists once the lessons are in, so everything it finds is already there.
     Nothing hides until this has run, so a page whose script never arrives
     still reads in full. */
  watchReveal = (root) => {
    if (!root || this.io) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches || !("IntersectionObserver" in window)) return;

    root.classList.add("js-reveal");
    this.io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-in");
          this.io.unobserve(entry.target);
        });
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.12 }
    );
    root.querySelectorAll("[data-reveal]").forEach((el) => this.io.observe(el));
  };

  render() {
    const { tenant, notFound, lessons, loading } = this.state;

    if (notFound) {
      return (
        <div className="page">
          <div className="container text-center">
            <p>{t("notFound")}</p>
          </div>
        </div>
      );
    }

    if (!tenant) return null;

    const teacherName = tenant.name || "";

    return (
      <>
        <div className="fv-stage mu mu-ground">
          <div className="fv-sky" aria-hidden="true">
            <span className="aur a1"></span>
            <span className="aur a2"></span>
            <span className="dots"></span>
          </div>

          <Header tenant={tenant} onStage />

          <div className="fv-head">
            <span className="mu-chip"><Icon name="play" className="chipi" /> {t("free")}</span>
            <h1>{t("freeVideos")}</h1>
            <p>{t("freeVideosLead", { name: teacherName })}</p>

            {!loading && lessons.length > 0 && (
              <span className="fv-count">
                {lessons.length === 1 ? t("freeLessonsOne") : t("freeLessonsCount", { n: lessons.length })}
              </span>
            )}
          </div>
        </div>

        <div className="fv-body mu">
          {loading ? (
            <div className="fv-grid">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <span className="fv-skel" key={i} style={{ "--i": i % 3 }}></span>
              ))}
            </div>
          ) : lessons.length > 0 ? (
            <div className="fv-grid" ref={this.watchReveal}>
              {lessons.map((lesson, i) => {
                const shot = lesson.thumb || lesson.image;
                return (
                  <article className="fv-card" key={lesson.id} data-reveal style={{ "--i": i % 3 }}>
                    <span className="fv-thumb">
                      {shot ? (
                        <img src={shot} alt="" loading="lazy" />
                      ) : (
                        // a lesson with no still of its own still needs to
                        // look like a video, not like a broken image
                        <span className="fv-noshot" aria-hidden="true">
                          <Icon name="play" />
                        </span>
                      )}
                      <span className="fv-play" aria-hidden="true">
                        <Icon name="play" />
                      </span>
                    </span>

                    <span className="fv-tx">
                      <h3>{lesson.lessonName}</h3>
                      <span className="fv-tag">
                        <Icon name="spark" />
                        {t("freeLesson")}
                      </span>
                    </span>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="fv-empty">
              <span className="ic" aria-hidden="true"><Icon name="play" /></span>
              <b>{t("noFreeLessonsYet")}</b>
              <p>{t("noFreeLessonsText")}</p>
              <Link to="/courses" className="mu-btn is-gold has-arw">
                {t("seeCourses")} <Icon name="arrow" className="arw" />
              </Link>
            </div>
          )}
        </div>

        <Footer tenant={tenant} />
      </>
    );
  }
}

export default FreeVideos;
